"use client";

import { cn } from "@repo/lib";
import Image from "next/image";
import { useEffect, useState } from "react";

// The discord-cdn.th.gl mirror ignores query strings (one Perma-Cached file per
// attachment), but the bot API hands out URLs with Discord's rotating ex/is/hm
// signature. Dropping it gives /_next/image one stable cache key per image
// instead of a new optimizer run every time the signature rotates.
function stableSrc(src: string): string {
  if (!src.startsWith("https://discord-cdn.th.gl/")) return src;
  const query = src.indexOf("?");
  return query === -1 ? src : src.slice(0, query);
}

export function PreviewImage({
  src: rawSrc,
  alt,
}: {
  src: string;
  alt?: string;
}) {
  const src = stableSrc(rawSrc);
  const [isHighlighted, setIsHighlighted] = useState(false);

  // ESC key listener
  useEffect(() => {
    if (!isHighlighted) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setIsHighlighted(false);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isHighlighted]);

  return (
    <>
      <Image
        key={src}
        src={src}
        alt={alt || ""}
        className={cn("rounded-lg object-cover cursor-zoom-in")}
        width={258}
        height={198}
        onClick={() => setIsHighlighted(true)}
      />
      <div
        className={cn(
          "fixed inset-0 z-50 flex items-center justify-center cursor-zoom-out transition-all",
          isHighlighted
            ? "bg-black/50 opacity-100 "
            : "opacity-0 pointer-events-none",
        )}
        onClick={() => setIsHighlighted(false)}
      >
        {isHighlighted && (
          <Image
            key={src + "large"}
            src={src}
            alt={alt || ""}
            className={cn("rounded-lg object-scale-down p-8 ")}
            fill
            // Full-size view: the original file, straight from the CDN.
            unoptimized
          />
        )}
      </div>
    </>
  );
}
